import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Send, Copy, CheckCircle2, Trash2, Eye, Plus, Save, Paperclip, Lock, Share2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtMDY = (s) => { if (!s) return ""; const p = String(s).split("-"); return p.length === 3 ? `${p[1]}/${p[2]}/${p[0]}` : s; };
const STATUS = {
  sent: { label: "Sent", cls: "bg-zinc-100 text-zinc-600" },
  in_progress: { label: "In progress", cls: "bg-amber-100 text-amber-700" },
  submitted: { label: "Submitted", cls: "bg-blue-100 text-blue-700" },
  complete: { label: "Complete", cls: "bg-green-100 text-green-700" },
};

export default function AdminOnboarding() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("list");
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ client_name: "", client_email: "" });
  const [sending, setSending] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [sendTarget, setSendTarget] = useState(null);
  const [sendEmail, setSendEmail] = useState("");
  const [sendMsg, setSendMsg] = useState("");
  const [sendingPdf, setSendingPdf] = useState(false);
  const openSend = (o) => { setSendTarget(o); setSendEmail(""); setSendMsg(""); };
  const doSend = async () => {
    if (!sendEmail.trim()) { toast.error("Enter a recipient email"); return; }
    setSendingPdf(true);
    try {
      await api.post(`/onboarding/${sendTarget.id}/send`, { to_email: sendEmail.trim(), message: sendMsg });
      toast.success(`Questionnaire PDF sent to ${sendEmail.trim()}`);
      setSendTarget(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    finally { setSendingPdf(false); }
  };
  const [questions, setQuestions] = useState([]);
  const [savingTpl, setSavingTpl] = useState(false);

  const load = () => api.get("/onboarding").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); api.get("/onboarding/template").then(({ data }) => setQuestions(data.questions)).catch(() => {}); }, []);

  const sendOnboarding = async () => {
    if (!form.client_name.trim() || !form.client_email.trim()) { toast.error("Enter client name and email"); return; }
    setSending(true);
    try {
      await api.post("/onboarding", form);
      toast.success(`Onboarding sent to ${form.client_email}`);
      setForm({ client_name: "", client_email: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    finally { setSending(false); }
  };

  const copyLink = (o) => { navigator.clipboard.writeText(`${window.location.origin}/onboarding/${o.token}`); toast.success("Link copied"); };
  const markComplete = async (o) => { try { await api.post(`/onboarding/${o.id}/complete`); toast.success("Marked complete"); load(); setViewing(null); } catch { toast.error("Failed"); } };
  const remove = async (o) => { try { await api.delete(`/onboarding/${o.id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); } };

  // Template builder helpers
  const updateQ = (i, patch) => setQuestions((qs) => qs.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  const addQ = () => setQuestions((qs) => [...qs, { id: `q_${Date.now()}`, section: "General", label: "New question", type: "text", required: false, options: [] }]);
  const removeQ = (i) => setQuestions((qs) => qs.filter((_, idx) => idx !== i));
  const moveQ = (i, dir) => setQuestions((qs) => { const a = [...qs]; const j = i + dir; if (j < 0 || j >= a.length) return a; [a[i], a[j]] = [a[j], a[i]]; return a; });
  const saveTemplate = async () => {
    setSavingTpl(true);
    try {
      const clean = questions.map((q) => ({ ...q, options: q.type === "select" ? (Array.isArray(q.options) ? q.options : String(q.options || "").split(",").map((s) => s.trim()).filter(Boolean)) : null }));
      const { data } = await api.put("/onboarding/template", { questions: clean });
      setQuestions(data.questions);
      toast.success("Template saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save template"); }
    finally { setSavingTpl(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-onboarding">
      <button onClick={() => navigate("/admin")} className="flex items-center gap-1 text-sm text-zinc-500 hover:text-primary mb-6"><ArrowLeft className="h-4 w-4" /> Back to dashboard</button>
      <h1 className="font-display font-black text-4xl tracking-tighter mb-6">Client Onboarding</h1>

      <div className="flex gap-1 mb-6 border-b border-zinc-200">
        <button onClick={() => setTab("list")} data-testid="tab-onboardings" className={`px-4 py-2 font-mono text-xs uppercase tracking-widest border-b-2 -mb-px ${tab === "list" ? "border-primary text-primary" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}>Onboardings ({items.length})</button>
        <button onClick={() => setTab("template")} data-testid="tab-template" className={`px-4 py-2 font-mono text-xs uppercase tracking-widest border-b-2 -mb-px ${tab === "template" ? "border-primary text-primary" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}>Questionnaire Template</button>
      </div>

      {tab === "list" ? (
        <>
          <div className="border border-zinc-900 bg-white p-5 mb-8">
            <h2 className="font-display font-bold mb-3">Send a new onboarding</h2>
            <div className="grid sm:grid-cols-3 gap-3">
              <Input data-testid="ob-client-name" placeholder="Client name" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="rounded-none border-zinc-300" />
              <Input data-testid="ob-client-email" placeholder="client@email.com" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} className="rounded-none border-zinc-300" />
              <Button onClick={sendOnboarding} disabled={sending} data-testid="ob-send-btn" className="rounded-none bg-primary hover:bg-[#5E1F92] text-white font-mono uppercase tracking-widest"><Send className="h-4 w-4 mr-1.5" /> Send</Button>
            </div>
          </div>

          <div className="border border-zinc-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-zinc-50 text-left font-mono text-xs uppercase tracking-widest text-zinc-500">
                <th className="px-4 py-3">Client</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Sent</th><th className="px-4 py-3 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {items.map((o) => (
                  <tr key={o.id} className="border-t border-zinc-100" data-testid={`ob-row-${o.id}`}>
                    <td className="px-4 py-3"><div className="font-medium">{o.client_name}</div><div className="text-zinc-400 text-xs">{o.client_email}</div></td>
                    <td className="px-4 py-3"><span className={`font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 ${(STATUS[o.status] || STATUS.sent).cls}`}>{(STATUS[o.status] || STATUS.sent).label}</span></td>
                    <td className="px-4 py-3 text-zinc-500">{new Date(o.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" data-testid={`ob-view-${o.id}`} onClick={() => setViewing(o)} className="rounded-none h-8 px-2" title="View responses"><Eye className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" data-testid={`ob-send-pdf-${o.id}`} onClick={() => openSend(o)} className="rounded-none h-8 px-2" title="Send questionnaire to…"><Share2 className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => copyLink(o)} className="rounded-none h-8 px-2" title="Copy link"><Copy className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => remove(o)} className="rounded-none h-8 px-2 text-red-500" title="Delete"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {items.length === 0 && <p className="p-10 text-center text-zinc-500">No onboardings sent yet.</p>}
          </div>
        </>
      ) : (
        <div>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-zinc-500">Customize the questions clients answer. Changes apply to onboardings sent after saving.</p>
            <div className="flex gap-2">
              <Button onClick={addQ} variant="outline" data-testid="add-question-btn" className="rounded-none border-zinc-900 font-mono uppercase tracking-widest text-xs"><Plus className="h-4 w-4 mr-1" /> Add</Button>
              <Button onClick={saveTemplate} disabled={savingTpl} data-testid="save-template-btn" className="rounded-none bg-primary hover:bg-[#5E1F92] text-white font-mono uppercase tracking-widest text-xs"><Save className="h-4 w-4 mr-1" /> {savingTpl ? "Saving…" : "Save Template"}</Button>
            </div>
          </div>
          <div className="space-y-3">
            {questions.map((q, i) => (
              <div key={q.id} className="border border-zinc-200 bg-white p-4" data-testid={`tpl-q-${i}`}>
                <div className="grid sm:grid-cols-12 gap-2 items-center">
                  <Input value={q.label} onChange={(e) => updateQ(i, { label: e.target.value })} placeholder="Question" className="sm:col-span-5 rounded-none border-zinc-300" />
                  <Input value={q.section || ""} onChange={(e) => updateQ(i, { section: e.target.value })} placeholder="Section" className="sm:col-span-2 rounded-none border-zinc-300" />
                  <Select value={q.type} onValueChange={(v) => updateQ(i, { type: v })}>
                    <SelectTrigger className="sm:col-span-2 rounded-none border-zinc-300"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["text", "textarea", "url", "date", "select", "file"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <label className="sm:col-span-2 flex items-center gap-1.5 text-xs font-mono uppercase text-zinc-500">
                    <input type="checkbox" checked={!!q.required} onChange={(e) => updateQ(i, { required: e.target.checked })} /> Required
                  </label>
                  <div className="sm:col-span-1 flex justify-end gap-1">
                    <button onClick={() => moveQ(i, -1)} className="text-zinc-400 hover:text-zinc-900 px-1">↑</button>
                    <button onClick={() => moveQ(i, 1)} className="text-zinc-400 hover:text-zinc-900 px-1">↓</button>
                    <button onClick={() => removeQ(i)} className="text-red-400 hover:text-red-600 px-1"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
                {q.type === "select" && (
                  <Input value={Array.isArray(q.options) ? q.options.join(", ") : (q.options || "")} onChange={(e) => updateQ(i, { options: e.target.value.split(",").map((s) => s.trim()) })} placeholder="Options, comma separated" className="mt-2 rounded-none border-zinc-300 text-sm" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="rounded-none max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{viewing?.client_name} — Responses</DialogTitle><DialogDescription>Client onboarding answers and uploaded files.</DialogDescription></DialogHeader>
          {viewing && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className={`font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 ${(STATUS[viewing.status] || STATUS.sent).cls}`}>{(STATUS[viewing.status] || STATUS.sent).label}</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" data-testid="ob-send-from-view" onClick={() => { openSend(viewing); }} className="rounded-none border-zinc-900 font-mono uppercase tracking-widest text-xs"><Share2 className="h-3.5 w-3.5 mr-1" /> Send to…</Button>
                  {viewing.status !== "complete" && <Button size="sm" onClick={() => markComplete(viewing)} data-testid="ob-complete-btn" className="rounded-none bg-green-600 hover:bg-green-700 text-white font-mono uppercase tracking-widest text-xs"><Lock className="h-3.5 w-3.5 mr-1" /> Mark complete</Button>}
                </div>
              </div>
              {viewing.questions.map((q) => {
                const ans = viewing.answers?.[q.id];
                const fl = viewing.files?.[q.id] || [];
                return (
                  <div key={q.id} className="border-b border-zinc-100 pb-3">
                    <p className="text-xs font-mono uppercase tracking-wider text-zinc-400">{q.section} · {q.label}</p>
                    {q.type === "file" ? (
                      fl.length ? (
                        <div className="mt-1.5 flex flex-wrap gap-3">
                          {fl.map((f) => {
                            const url = `${API}/onboarding/${viewing.id}/files/${f.file_id}`;
                            const isImg = (f.content_type || "").startsWith("image/");
                            return (
                              <a key={f.file_id} href={url} target="_blank" rel="noreferrer" title={f.filename} className="block group" data-testid={`ob-file-${f.file_id}`}>
                                {isImg ? (
                                  <img src={url} alt={f.filename} className="h-20 w-20 object-cover border border-zinc-200 group-hover:border-primary transition-colors" />
                                ) : (
                                  <span className="flex items-center gap-2 text-sm text-primary hover:underline"><Paperclip className="h-3.5 w-3.5" /> {f.filename}</span>
                                )}
                              </a>
                            );
                          })}
                        </div>
                      ) : <p className="mt-1 text-sm text-zinc-400">No files uploaded</p>
                    ) : (
                      <p className="mt-1 text-sm text-zinc-800 whitespace-pre-wrap">{q.type === "date" && ans ? fmtMDY(ans) : (ans || <span className="text-zinc-400">No answer</span>)}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!sendTarget} onOpenChange={(o) => !o && setSendTarget(null)}>
        <DialogContent className="rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle>Send Questionnaire To</DialogTitle>
            <DialogDescription>Email the completed onboarding PDF for {sendTarget?.client_name} to a third party (developer, designer, partner).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest">Recipient email</Label>
              <Input data-testid="send-pdf-email" type="email" value={sendEmail} onChange={(e) => setSendEmail(e.target.value)} placeholder="dev@partner.com" className="mt-1.5 rounded-none border-zinc-300" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest">Message (optional)</Label>
              <Textarea data-testid="send-pdf-message" value={sendMsg} onChange={(e) => setSendMsg(e.target.value)} placeholder="Add a short note…" className="mt-1.5 rounded-none border-zinc-300 min-h-[80px]" />
            </div>
            <Button onClick={doSend} disabled={sendingPdf} data-testid="send-pdf-confirm" className="w-full rounded-none bg-primary hover:bg-[#5E1F92] text-white font-mono uppercase tracking-widest h-11">
              <Send className="h-4 w-4 mr-1.5" /> {sendingPdf ? "Sending…" : "Send PDF"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
